import { useSyncExternalStore } from 'react'
import {
    ConfirmationModal,
    DialogModal,
    DrawerModal,
    FloaterModal,
    FloatingRoomModal,
} from '../../../types/state'
import { ModalEvent, ModalKind } from '../../constants/enums'
import { FLOATING_PREVIEW } from '../../constants/floaters'
import {
    getModalStack as getModalStackState,
    getModalState,
} from '../../state/getState'
import { generateModalKey } from '../../state/internal/modals'
import { pubSubModal, pubSubModalStack } from '../../state/pubSub'

const getDialogState = () => getModalState().dialog
const getDialogStateSelector =
    <T extends keyof DialogModal>(key: T) =>
    () =>
        getModalState().dialog[key]
const getDrawerState = () => getModalState().drawer
const getDrawerStateSelector =
    <T extends keyof DrawerModal>(key: T) =>
    () =>
        getModalState().drawer[key]
const getConfirmationState = () => getModalState().confirmation
const getQuickMenuState = () => getModalState().quick_menu
const getConfirmationStateSelector =
    <T extends keyof ConfirmationModal>(key: T) =>
    () =>
        getModalState().confirmation[key]
const getFloatersState = () => getModalState().floater
export const getFloaterState = (floaterKey: string) => () =>
    getModalState().floater[floaterKey]
const getFloaterStateSelector = (floaterKey: string, attribute: string) => () =>
    getModalState().floater[floaterKey]?.[attribute]

// const getStickyPreviewState = () => getModalState().floater[STICKY_PREVIEW]
const getFloatingRoomShowState = () => getModalState().floating_room.show

const getFloatingRoomStateSelector =
    <T extends keyof FloatingRoomModal>(key: T) =>
    () =>
        getModalState().floating_room[key]
const getModalStack = (modalKey: string) => () =>
    getModalStackState().byKey[modalKey]

const subDialog = (callback: () => void) =>
    pubSubModal.on(ModalEvent.UPDATE_DIALOG, callback)
const subDrawer = (callback: () => void) =>
    pubSubModal.on(ModalEvent.UPDATE_DRAWER, callback)
const subConfirmation = (callback: () => void) =>
    pubSubModal.on(ModalEvent.UPDATE_CONFIRMATION, callback)
const subQuickMenu = (callback: () => void) =>
    pubSubModal.on(ModalEvent.UPDATE_QUICK_MENU, callback)
const subFloater = (callback: () => void) =>
    pubSubModal.on(ModalEvent.UPDATE_FLOATER, callback)
export const subFloatingRoom = (callback: () => void) =>
    pubSubModal.on(ModalEvent.UPDATE_FLOATING_ROOM, callback)
const subModalStack = (callback: () => void) =>
    pubSubModalStack.on(ModalEvent.UPDATE_MODAL_STACK, callback)

export const useDialog = () => {
    const dialog = useSyncExternalStore(
        subDialog,
        getDialogState,
        getDialogState,
    )

    return dialog
}
export const useDialogSelector = <T extends keyof DialogModal>(
    key: T,
): DialogModal[T] => {
    const showDialog = useSyncExternalStore(
        subDialog,
        getDialogStateSelector(key),
        getDialogStateSelector(key),
    )

    return showDialog
}

export const useDrawer = () => {
    const drawer = useSyncExternalStore(
        subDrawer,
        getDrawerState,
        getDrawerState,
    )

    return drawer
}
export const useDrawerSelector = <T extends keyof DrawerModal>(
    key: T,
): DrawerModal[T] => {
    const showDrawer = useSyncExternalStore(
        subDrawer,
        getDrawerStateSelector(key),
        getDrawerStateSelector(key),
    )

    return showDrawer
}

export const useConfirmation = () => {
    const confirmation = useSyncExternalStore(
        subConfirmation,
        getConfirmationState,
        getConfirmationState,
    )

    return confirmation
}
export const useQuickMenu = () => {
    const quickMenu = useSyncExternalStore(
        subQuickMenu,
        getQuickMenuState,
        getQuickMenuState,
    )

    return quickMenu
}

export const useConfirmationSelector = <T extends keyof ConfirmationModal>(
    key: T,
): ConfirmationModal[T] => {
    const showConfirmation = useSyncExternalStore(
        subConfirmation,
        getConfirmationStateSelector(key),
        getConfirmationStateSelector(key),
    )

    return showConfirmation
}

export const useFloater = (
    modalKey: string,
): [FloaterModal, stackPosition: number] => {
    const floater = useSyncExternalStore(
        subFloater,
        getFloaterState(modalKey),
        getFloaterState(modalKey),
    )
    const stackPosition = useSyncExternalStore(
        subModalStack,
        getModalStack(modalKey),
        getModalStack(modalKey),
    )
    return [floater, stackPosition]
}
export const useFloaters = (): Array<FloaterModal> => {
    const floaters = useSyncExternalStore(
        subFloater,
        getFloatersState,
        getFloatersState,
    )
    const floaterList = Object.values(floaters).filter(
        (floater) => floater.show, // && floater.id !== FLOATING_ROOM,
    )
    return floaterList
}
export const useFloatingRoomStackPosition = (): number =>
    useSyncExternalStore(
        subModalStack,
        getModalStack(ModalKind.FloatingRoom),
        getModalStack(ModalKind.FloatingRoom),
    )
export const useFloatingRoomId = (): number => {
    const floatingRoomShow = useSyncExternalStore(
        subFloatingRoom,
        getFloatingRoomShowState,
        getFloatingRoomShowState,
    )

    return floatingRoomShow ? getModalState().floating_room.id : null
}
export const useFloatingRoomStateSelector = <T extends keyof FloatingRoomModal>(
    key: T,
) => {
    const floatingRoomStateValue = useSyncExternalStore(
        subFloatingRoom,
        getFloatingRoomStateSelector(key),
        getFloatingRoomStateSelector(key),
    )

    return floatingRoomStateValue
}

export const useFloatingPreviewSelector = <T extends keyof FloaterModal>(
    id: number,
    attribute: T,
) => {
    const key = generateModalKey(FLOATING_PREVIEW, id)
    return useSyncExternalStore(
        subFloater,
        getFloaterStateSelector(key, attribute),
        getFloaterStateSelector(key, attribute),
    )
}
